<template>
    <div class="account-broadcast-calls">
        <v-progress-linear
            v-if="loading"
            :indeterminate="true"
            color="success"
        ></v-progress-linear>
        <div v-if="!loading">
            <div class="account-broadcast-calls-list">
                <div v-if="!calls.length" class="account-comm-empty">
                    No broadcast calls
                </div>
                <div
                    v-for="(call, index) in calls"
                    :key="call.id || index"
                    class="account-comm-item"
                    :class="{ 'account-comm-item--open': call.expanded }"
                >
                    <div class="account-comm-item-header" @click="toggle(call)">
                        <div class="account-comm-item-avatar account-comm-item-avatar--call">
                            <v-icon small dark>mdi-phone-in-talk</v-icon>
                        </div>
                        <div class="account-comm-item-summary">
                            <div class="account-comm-item-title-row">
                                <span class="account-comm-item-title">
                                    {{ call.broadcastName || "Broadcast call" }}
                                </span>
                                <v-icon
                                    v-if="call.recording"
                                    small
                                    color="#607d8b"
                                    class="account-comm-attach-icon"
                                >
                                    mdi-volume-high
                                </v-icon>
                            </div>
                            <div class="account-comm-item-preview">
                                {{ formatPhone(call.to) || formatPhone(call.from) }}
                            </div>
                        </div>
                        <div class="account-comm-item-meta">
                            <div class="account-comm-item-date">{{ call.date | time }}</div>
                            <v-chip
                                small
                                text-color="white"
                                :color="statusColor(call)"
                                class="account-comm-chip"
                            >
                                {{ call.status || call.hangupCause || "Unknown" }}
                            </v-chip>
                        </div>
                        <v-icon class="account-comm-item-chevron">
                            {{ call.expanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                        </v-icon>
                    </div>

                    <div v-if="call.expanded" class="account-comm-item-body">
                        <div class="account-comm-detail-grid">
                            <div class="account-comm-detail" v-if="call.broadcastName">
                                <span class="account-comm-detail-label">Broadcast</span>
                                <span class="account-comm-detail-value">{{ call.broadcastName }}</span>
                            </div>
                            <div class="account-comm-detail" v-if="call.from">
                                <span class="account-comm-detail-label">From</span>
                                <span class="account-comm-detail-value">{{ formatPhone(call.from) }}</span>
                            </div>
                            <div class="account-comm-detail" v-if="call.to">
                                <span class="account-comm-detail-label">To</span>
                                <span class="account-comm-detail-value">{{ formatPhone(call.to) }}</span>
                            </div>
                            <div class="account-comm-detail">
                                <span class="account-comm-detail-label">Hangup Status</span>
                                <span class="account-comm-detail-value">{{ call.status || call.hangupCause || "Unknown" }}</span>
                            </div>
                            <div class="account-comm-detail" v-if="call.listenedToMessage">
                                <span class="account-comm-detail-label">Listened</span>
                                <span class="account-comm-detail-value">Yes</span>
                            </div>
                        </div>

                        <div class="account-comm-recording">
                            <audio
                                v-if="call.recording"
                                controls
                                :src="call.recording"
                            ></audio>
                            <div v-else class="grey--text">No recording available</div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="pages > 1" class="account-broadcast-calls-pagination">
                <v-pagination
                    v-model="pagination.page"
                    :length="pages"
                    :total-visible="7"
                ></v-pagination>
            </div>
        </div>
    </div>
</template>
<script>
import { formatPhoneNumber } from "../../utils/helpers.js";

const PAGE_SIZE = 10;

export default {
    data() {
        return {
            loading: true,
            calls: [],
            totalCount: 0,
            pagination: {
                rowsPerPage: PAGE_SIZE,
                page: 1,
            },
        };
    },
    computed: {
        pages() {
            return this.pagination.rowsPerPage
                ? Math.ceil(this.totalCount / this.pagination.rowsPerPage)
                : 0;
        },
    },
    watch: {
        "pagination.page"() {
            this.loadCalls();
        },
    },
    async created() {
        await this.loadCallsCount();
        await this.loadCalls();
    },
    methods: {
        async loadCallsCount() {
            try {
                this.totalCount = await this.$http.get(
                    "api/broadcast/getCallsCountForAccount?accountID=" +
                        this.$route.params.id
                );
            } catch (e) {
                this.totalCount = 0;
            }
        },
        async loadCalls() {
            this.loading = true;

            try {
                const skip = (this.pagination.page - 1) * PAGE_SIZE;
                const calls = await this.$http.get(
                    "api/broadcast/getCallsForAccount?accountID=" +
                        this.$route.params.id +
                        "&skip=" +
                        skip +
                        "&top=" +
                        PAGE_SIZE
                );
                this.calls = calls || [];
                this.$emit("loaded", this.totalCount);
            } catch (e) {
                this.calls = [];
                this.$emit("loaded", 0);
            }

            this.loading = false;
        },
        toggle(call) {
            this.$set(call, "expanded", !call.expanded);
        },
        formatPhone(value) {
            return value ? formatPhoneNumber(value) : "";
        },
        statusColor(call) {
            const status = (
                (call && (call.status || call.hangupCause)) ||
                ""
            ).toUpperCase();

            if (status.indexOf("ANSWER") !== -1 || status.indexOf("COMPLETE") !== -1) {
                return "success";
            }
            if (status.indexOf("FAIL") !== -1 || status.indexOf("BUSY") !== -1 || status.indexOf("NO") !== -1) {
                return "#e57373";
            }

            return "#90a4ae";
        },
    },
};
</script>
<style>
.account-broadcast-calls {
    padding: 6px;
}

.account-broadcast-calls-list {
    max-height: 500px;
    overflow-y: auto;
}

.account-broadcast-calls-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 0 2px;
    border-top: 1px solid #eceff1;
    margin-top: 4px;
}

.account-comm-item-avatar--call {
    background: #5c6bc0;
}

.account-comm-recording {
    padding: 4px 0 0;
}

.account-comm-recording audio {
    width: 100%;
    max-width: 420px;
    height: 32px;
}
</style>
